import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
  fetchLinkedScenarios,
  bulkLinkScenarios,
  unlinkScenario
} from '../../api/issueLinkApi'

// ============================================================================
// ASYNC THUNKS
// ============================================================================

/**
 * FETCH LINKED SCENARIOS
 * Fetches all test scenarios linked to a Jira issue key.
 * Returns scenario details with per-run execution status counts.
 */
export const fetchLinkedScenariosAsync = createAsyncThunk(
  'issueLinks/fetchLinkedScenarios',
  async (jiraIssueKey, { rejectWithValue }) => {
    try {
      return await fetchLinkedScenarios(jiraIssueKey)
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to fetch linked scenarios',
        status: error.response?.status
      })
    }
  }
)

/**
 * BULK LINK SCENARIOS
 * Links a Jira issue to multiple test scenarios in one request.
 * After success, re-fetches the linked scenarios list.
 */
export const bulkLinkScenariosAsync = createAsyncThunk(
  'issueLinks/bulkLinkScenarios',
  async ({ testScenarioIds, jiraIssueKey }, { rejectWithValue, dispatch }) => {
    try {
      const result = await bulkLinkScenarios({ testScenarioIds, jiraIssueKey })
      // Re-fetch linked scenarios to get updated list with full details
      dispatch(fetchLinkedScenariosAsync(jiraIssueKey))
      return result
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to link scenarios',
        status: error.response?.status
      })
    }
  }
)

/**
 * UNLINK SCENARIO
 * Removes a specific Jira issue link from a test scenario.
 * Optimistically removes the scenario from the list, then re-fetches to confirm.
 */
export const unlinkScenarioAsync = createAsyncThunk(
  'issueLinks/unlinkScenario',
  async ({ testScenarioId, linkId, jiraIssueKey }, { rejectWithValue, dispatch }) => {
    try {
      await unlinkScenario(testScenarioId, linkId)
      // Re-fetch linked scenarios to get updated list
      dispatch(fetchLinkedScenariosAsync(jiraIssueKey))
      return { testScenarioId, linkId }
    } catch (error) {
      return rejectWithValue({
        message: error.response?.data?.message || error.message || 'Failed to unlink scenario',
        status: error.response?.status
      })
    }
  }
)

// ============================================================================
// SLICE
// ============================================================================

const initialState = {
  // Current Jira issue key from AP context (e.g., "PROJ-123")
  currentIssueKey: null,

  // Scenarios linked to the current issue (from GET /jira-issues/{key}/testscenarios)
  linkedScenarios: [],

  // Loading states
  loading: false,
  unlinkLoading: null, // Stores the linkId being unlinked (for per-row spinner)
  linkingInProgress: false,

  // Error state
  error: null
}

const issueLinkSlice = createSlice({
  name: 'issueLinks',
  initialState,
  reducers: {
    /**
     * Set the current Jira issue key (from AP context on mount)
     */
    setCurrentIssueKey(state, action) {
      state.currentIssueKey = action.payload
    },
    /**
     * Reset all issue link state (e.g., when navigating away)
     */
    clearIssueLinkState(state) {
      Object.assign(state, initialState)
    }
  },
  extraReducers: (builder) => {
    // --- Fetch linked scenarios ---
    builder
      .addCase(fetchLinkedScenariosAsync.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(fetchLinkedScenariosAsync.fulfilled, (state, action) => {
        state.loading = false
        state.linkedScenarios = action.payload || []
      })
      .addCase(fetchLinkedScenariosAsync.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload?.message || 'Failed to fetch linked scenarios'
      })

    // --- Bulk link scenarios ---
    builder
      .addCase(bulkLinkScenariosAsync.pending, (state) => {
        state.linkingInProgress = true
        state.error = null
      })
      .addCase(bulkLinkScenariosAsync.fulfilled, (state) => {
        state.linkingInProgress = false
      })
      .addCase(bulkLinkScenariosAsync.rejected, (state, action) => {
        state.linkingInProgress = false
        state.error = action.payload?.message || 'Failed to link scenarios'
      })

    // --- Unlink scenario ---
    builder
      .addCase(unlinkScenarioAsync.pending, (state, action) => {
        // Store the linkId being unlinked for per-row spinner
        state.unlinkLoading = action.meta.arg.linkId
        state.error = null
      })
      .addCase(unlinkScenarioAsync.fulfilled, (state, action) => {
        state.unlinkLoading = null
        // Optimistically remove the scenario from the list by matching testScenarioId
        const { testScenarioId } = action.payload
        state.linkedScenarios = state.linkedScenarios.filter(
          s => s.id !== testScenarioId
        )
      })
      .addCase(unlinkScenarioAsync.rejected, (state, action) => {
        state.unlinkLoading = null
        state.error = action.payload?.message || 'Failed to unlink scenario'
      })
  }
})

// ============================================================================
// EXPORTS
// ============================================================================

export const { setCurrentIssueKey, clearIssueLinkState } = issueLinkSlice.actions

// Selectors
export const selectCurrentIssueKey = (state) => state.issueLinks?.currentIssueKey || null
export const selectLinkedScenarios = (state) => state.issueLinks?.linkedScenarios || []
export const selectIssueLinkLoading = (state) => state.issueLinks?.loading || false
export const selectUnlinkLoading = (state) => state.issueLinks?.unlinkLoading || null
export const selectLinkingInProgress = (state) => state.issueLinks?.linkingInProgress || false
export const selectIssueLinkError = (state) => state.issueLinks?.error || null

export default issueLinkSlice.reducer
